import { useState, useRef, useMemo, useEffect } from 'react';
import { X, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import IssueDetailFields from './IssueDetailFields';
import IssueTemplateSelector from './IssueTemplateSelector';
import ParentIssueSelect from './ParentIssueSelect';
import { useCreateIssue, useUploadAttachments } from '../../hooks/useIssues';
import { useFieldConfig } from '../../hooks/useIssueTypeFieldConfig';
import type { ProjectIssueType } from '../../hooks/useProjectIssueTypes';
import type { ProjectMember } from '../../services/projectService';

interface Props {
  projectId: string;
  members: ProjectMember[];
  projectIssueTypes?: ProjectIssueType[];
  /** 모달 오픈 시 기본 선택 유형 (없으면 첫 유형 또는 'Task') */
  defaultTypeName?: string;
  onClose: () => void;
}

export default function IssueCreateModal({
  projectId,
  members,
  projectIssueTypes,
  defaultTypeName,
  onClose,
}: Props) {
  const createIssueMutation = useCreateIssue(projectId);
  const uploadMutation = useUploadAttachments(projectId);

  const [title, setTitle] = useState('');
  const [typeName, setTypeName] = useState<string>(defaultTypeName ?? 'Task');
  const [parentIssueId, setParentIssueId] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  // 시스템 필드(우선순위/담당자/시작일/마감일/설명/...) + 커스텀 필드 통합 상태
  const [fields, setFields] = useState<Record<string, unknown>>({
    priority: 'MEDIUM',
    description: '',
    customFields: {},
  });

  // 첫 진입 시 기본 타입을 프로젝트 default로 보정
  useEffect(() => {
    if (!projectIssueTypes?.length) return;
    const exists = projectIssueTypes.some((t) => t.name === typeName);
    if (!exists) {
      const def = projectIssueTypes.find((t) => t.isDefault) ?? projectIssueTypes[0];
      setTypeName(def.name);
    }
  }, [projectIssueTypes, typeName]);

  const selectedTypeId = projectIssueTypes?.find((t) => t.name === typeName)?.issueTypeId;
  const { data: fieldConfig } = useFieldConfig(selectedTypeId);

  // title 필드는 모달 상단에 명시적으로 노출하므로 sections에서 제외 (중복 방지)
  const sectionsToRender = useMemo(() => {
    if (!fieldConfig) return [];
    return fieldConfig.sections
      .map((s) => ({
        ...s,
        fields: s.fields.filter((f) => f.field.fieldKey !== 'title'),
      }))
      .filter((s) => s.fields.length > 0);
  }, [fieldConfig]);

  const memberList = useMemo(
    () => members.map((m) => ({ id: m.user.id, name: m.user.name })),
    [members],
  );

  const handleFieldChange = (key: string, value: unknown, isSystem: boolean) => {
    if (isSystem) {
      setFields((prev) => ({ ...prev, [key]: value }));
    } else {
      setFields((prev) => ({
        ...prev,
        customFields: { ...((prev.customFields as Record<string, unknown>) ?? {}), [key]: value },
      }));
    }
  };

  const handleAddFiles = (files: FileList | null) => {
    if (!files) return;
    setPendingFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleRemoveFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createIssueMutation.isPending && !uploadMutation.isPending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, createIssueMutation.isPending, uploadMutation.isPending]);

  const handleCreate = async () => {
    if (isSubmittingRef.current) return;
    if (!title.trim() || createIssueMutation.isPending || uploadMutation.isPending) return;
    isSubmittingRef.current = true;
    try {
      const customFields = (fields.customFields as Record<string, unknown>) ?? {};
      const issue = await createIssueMutation.mutateAsync({
        title: title.trim(),
        issueType: typeName,
        parentIssueId: parentIssueId || undefined,
        description: (fields.description as string)?.trim() || undefined,
        priority: (fields.priority as string) || 'MEDIUM',
        assigneeId: (fields.assigneeId as string) || undefined,
        startDate: (fields.startDate as string) || undefined,
        dueDate: (fields.dueDate as string) || undefined,
        labels: (fields.labels as string[]) || undefined,
        storyPoints:
          fields.storyPoints !== undefined && fields.storyPoints !== null && fields.storyPoints !== ''
            ? Number(fields.storyPoints)
            : undefined,
        customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
      });

      if (pendingFiles.length > 0) {
        try {
          await uploadMutation.mutateAsync({ issueId: issue.id, files: pendingFiles });
          toast.success(`작업이 생성되고 ${pendingFiles.length}개 파일이 첨부되었습니다.`);
        } catch {
          toast.success('작업이 생성되었지만 파일 업로드에 실패했습니다.');
        }
      } else {
        toast.success('작업이 생성되었습니다.');
      }
      onClose();
    } catch {
      toast.error('작업 생성에 실패했습니다.');
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const isPending = createIssueMutation.isPending || uploadMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">작업 생성</h2>
          <button
            onClick={onClose}
            disabled={isPending}
            className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — 스크롤 */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          {/* 작업 유형 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">작업 유형 *</label>
            <select
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              {projectIssueTypes?.map((t) => (
                <option key={t.issueTypeId} value={t.name}>
                  {t.name}
                  {t.isDefault ? ' (기본)' : ''}
                </option>
              )) ?? (
                <>
                  <option value="Task">Task</option>
                  <option value="Bug">Bug</option>
                  <option value="Story">Story</option>
                  <option value="Epic">Epic</option>
                  <option value="Subtask">Subtask</option>
                </>
              )}
            </select>
          </div>

          {/* 제목 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">제목 *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="작업 제목을 입력하세요"
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
          </div>

          {/* 상위 작업 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">상위 작업</label>
            <ParentIssueSelect
              projectId={projectId}
              issueType={typeName}
              value={parentIssueId}
              onChange={setParentIssueId}
            />
          </div>

          {/* 템플릿 (설명 자동 채움) */}
          <div>
            <IssueTemplateSelector
              projectId={projectId}
              onSelectTemplate={(content, templateName) => {
                setFields((prev) => {
                  const cur = (prev.description as string) ?? '';
                  const next = !cur || cur.trim() === '' ? content : `${cur}\n\n${content}`;
                  return { ...prev, description: next };
                });
                toast.success(`'${templateName}' 템플릿이 적용되었습니다.`);
              }}
            />
          </div>

          {/* 유형별 모든 속성 (시스템 + 커스텀, title은 위에서 처리) */}
          {sectionsToRender.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <IssueDetailFields
                issue={fields}
                sections={sectionsToRender}
                isEditing
                onChange={handleFieldChange}
                members={memberList}
              />
            </div>
          )}

          {/* 첨부 */}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">첨부</label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Paperclip size={14} />
                파일 선택
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.zip"
                hidden
                onChange={(e) => {
                  handleAddFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>
            {pendingFiles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {pendingFiles.map((file, idx) => (
                  <span
                    key={`${file.name}-${idx}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded-md"
                  >
                    <Paperclip size={12} />
                    {file.name}
                    <button
                      onClick={() => handleRemoveFile(idx)}
                      className="ml-0.5 text-gray-400 hover:text-gray-600"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">선택된 파일이 없습니다.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || isPending}
            className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  );
}
